import { useEffect, useRef, useState } from "react"
import { NavLink, useLocation } from "react-router-dom"
import { ChevronRight, ChevronsUpDown } from "lucide-react"

import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarRail,
  useSidebar,
} from "@/components/ui/sidebar"
import { cn } from "@/lib/utils"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"

import { AccountSwitcher } from "@/app/layouts/account"
import { useNavGroups } from "@/app/layouts/nav"
import { LogoInstitucional } from "@/modules/cadastro/components/LogoInstitucional"
import { useInstitucionalPublico } from "@/modules/cadastro/hooks/useInstitucionalPublico"

export function AppSidebar() {
  const groups = useNavGroups()
  const location = useLocation()
  const { state, isMobile, setOpenMobile, toggleSidebar } = useSidebar()
  const { data: institucional } = useInstitucionalPublico()
  const siglaInstitucional = institucional?.sigla?.trim() || null
  const [abertos, setAbertos] = useState<Record<string, boolean>>({})

  /** Sidebar recolhida (ícone) no desktop: submenus viram flyout. */
  const colapsado = state === "collapsed" && !isMobile

  /** Match exato para itens de folha (apenas a rota exata fica ativa). */
  const ativoExato = (to: string) =>
    to === "/" ? location.pathname === "/" : location.pathname === to

  /** Match por prefixo para cabeçalhos de grupo (mantém o grupo aberto/ativo
   *  quando qualquer rota filha está selecionada). */
  const ativoInicia = (to: string) =>
    to === "/"
      ? location.pathname === "/"
      : location.pathname === to ||
        location.pathname.startsWith(`${to}/`)

  const moduloAtivo = groups.find((group) =>
    group.items.some((item) => ativoInicia(item.to)),
  )?.label
  const anterior = useRef<string | undefined>(undefined)

  useEffect(() => {
    if (moduloAtivo && moduloAtivo !== anterior.current) {
      setAbertos((f) => ({ ...f, [moduloAtivo]: true }))
    }
    anterior.current = moduloAtivo
  }, [moduloAtivo])

  const aberto = (label: string) => abertos[label] ?? false
  const alternar = (label: string) => setAbertos((f) => ({ ...f, [label]: !aberto(label) }))

  return (
    <Sidebar collapsible="icon">
      <SidebarHeader>
        <SidebarMenu>
          <SidebarMenuItem>
           
            <SidebarMenuButton
              size="lg"
              aria-label={state === "collapsed" ? "Expandir menu" : "Recolher menu"}
              className="data-open:bg-sidebar-accent data-open:text-sidebar-accent-foreground"
              onClick={toggleSidebar}
            >
              <LogoInstitucional
                className="size-8 shrink-0 rounded-md object-contain"
                fallback={
                  <div className="flex size-8 shrink-0 items-center justify-center rounded-md text-sidebar-primary-foreground bg-blue-200">
                    
                    <img src="/favicon-sigi-final.svg" alt="SIGI" className="size-7" />
                  </div>
                }
              />
              <div className="grid flex-1 text-left leading-tight group-data-[collapsible=icon]:hidden">
                <span className="truncate text-sm font-semibold">SIGI</span>
                <span className="truncate text-xs text-sidebar-foreground/70">
                  {siglaInstitucional ? `Gestão Interna · ${siglaInstitucional}` : "Gestão Interna"}
                </span>
              </div>
              <ChevronsUpDown className="ml-auto size-4 shrink-0 text-sidebar-foreground/70 group-data-[collapsible=icon]:hidden" />
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>

      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu className="gap-1.5">
              {groups.map((group) => {
                if (!group.icon) {
                  return group.items.map((item) => (
                    <SidebarMenuItem key={item.to}>
                      <SidebarMenuButton
                        isActive={ativoExato(item.to) && item.to !== "/"}
                        tooltip={item.label}
                        onClick={() => setOpenMobile(false)}
                        render={<NavLink to={item.to} end={item.to === "/"} />}
                      >
                        <item.icon />
                        <span>{item.label}</span>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  ))
                }

                const ModuloIcon = group.icon

                if (group.items.length < 2) {
                  const item = group.items[0]
                  return (
                    <SidebarMenuItem key={group.label}>
                      <SidebarMenuButton
                        isActive={ativoExato(item.to)}
                        tooltip={group.label}
                        onClick={() => setOpenMobile(false)}
                        render={<NavLink to={item.to} end={item.to === "/"} />}
                      >
                        <ModuloIcon />
                        <span>{group.label}</span>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  )
                }

                const expandido = aberto(group.label)

                // Recolhida: o SidebarMenuSub é oculto, então o grupo abre um
                // flyout com os itens (também ao passar o mouse).
                if (colapsado) {
                  const ativo = group.items.some((item) => ativoInicia(item.to))
                  return (
                    <SidebarMenuItem key={group.label}>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild openOnHover delay={80}>
                          <SidebarMenuButton isActive={ativo}>
                            <ModuloIcon />
                            <span>{group.label}</span>
                          </SidebarMenuButton>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent
                          side="right"
                          align="start"
                          sideOffset={8}
                          className="w-52"
                        >
                          <DropdownMenuLabel className="truncate">{group.label}</DropdownMenuLabel>
                          <DropdownMenuSeparator />
                          {group.items.map((item) => (
                            <DropdownMenuItem
                              key={item.to}
                              render={<NavLink to={item.to} end={item.to === "/"} />}
                              onClick={() => setOpenMobile(false)}
                              className={cn(ativoExato(item.to) && "bg-accent text-accent-foreground")}
                            >
                              {item.label}
                            </DropdownMenuItem>
                          ))}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </SidebarMenuItem>
                  )
                }

                return (
                  <SidebarMenuItem key={group.label}>
                    <SidebarMenuButton
                      isActive={group.items.some((item) => ativoInicia(item.to))}
                      tooltip={group.label}
                      aria-expanded={expandido}
                      onClick={() => alternar(group.label)}
                    >
                      <ModuloIcon />
                      <span>{group.label}</span>
                      <ChevronRight
                        className={cn(
                          "ml-auto size-4 shrink-0 text-sidebar-foreground/60 transition-transform duration-200",
                          expandido && "rotate-90",
                        )}
                      />
                    </SidebarMenuButton>
                    {expandido && (
                      <SidebarMenuSub>
                        {group.items.map((item) => (
                          <SidebarMenuSubItem key={item.to}>
                            <SidebarMenuSubButton
                              isActive={ativoExato(item.to)}
                              onClick={() => setOpenMobile(false)}
                              render={<NavLink to={item.to} end={item.to === "/"} />}
                            >
                              <span>{item.label}</span>
                            </SidebarMenuSubButton>
                          </SidebarMenuSubItem>
                        ))}
                      </SidebarMenuSub>
                    )}
                  </SidebarMenuItem>
                )
              })}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      <SidebarFooter>
        <SidebarMenu>
          <SidebarMenuItem>
            <AccountSwitcher />
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>

      <SidebarRail />
    </Sidebar>
  )
}
